import { User } from '@/types/user.type'
import { Token } from '@/types/token.type'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { fNumber } from '@/utils/format-number'
import { UserToken } from '@/types/user-token.type'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Search, User as UserIcon, Wallet } from 'lucide-react'
import CustomPagination from '@/components/pagination/custom-pagination'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const EMPTY_USER_TOKENS: UserToken[] = []
const EMPTY_TOKENS: Token[] = []

interface UserSelectionTableProps {
  users: User[]
  loading?: boolean
  selectedUserId?: string
  onUserSelect: (userId: string) => void
  search: string
  onSearchChange: (search: string) => void
  pageMeta: {
    total: number
    page: number
    limit: number
  }
  onPageChange: (page: number) => void
  title: string
  placeholder?: string
  userTokens?: UserToken[]
  tokens?: Token[]
  selectedToken?: string
}

export default function UserSelectionTable({
  users,
  loading = false,
  selectedUserId,
  onUserSelect,
  search,
  onSearchChange,
  pageMeta,
  onPageChange,
  title,
  placeholder = 'ค้นหาด้วยรหัสสมาชิก...',
  userTokens = EMPTY_USER_TOKENS,
  tokens = EMPTY_TOKENS,
  selectedToken,
}: UserSelectionTableProps) {
  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return user.displayName || '-'
  }

  const getRoleBadge = (role: 'USER' | 'SHOP' | 'ADMIN') => {
    const roleConfig = {
      USER: { label: 'ผู้ใช้', variant: 'default' as const },
      ADMIN: { label: 'แอดมิน', variant: 'destructive' as const },
      SHOP: { label: 'ร้านค้า', variant: 'secondary' as const },
    }

    const config = roleConfig[role]
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    )
  }

  const getSelectedUserTokens = () => {
    if (!selectedUserId || !selectedToken) return []

    return userTokens.filter((ut) => ut.userId === selectedUserId)
  }

  const getSelectedUser = () => {
    return users.find((u) => u.uid === selectedUserId)
  }

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-0 h-full flex flex-col">
          <div className="p-4 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-4 w-4" />
              <h3 className="font-medium">{title}</h3>
            </div>
            <Input placeholder={placeholder} disabled className="mb-4" />
            <div className="flex-1 flex flex-col">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>ชื่อผู้ใช้</TableHead>
                    <TableHead>อีเมล</TableHead>
                    <TableHead>รหัสสมาชิก</TableHead>
                    <TableHead>บทบาท</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (users.length === 0) {
    return (
      <Card className="h-full">
        <CardContent className="p-0 h-full flex flex-col">
          <div className="p-4 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-4 w-4" />
              <h3 className="font-medium">{title}</h3>
            </div>
            <Input
              placeholder={placeholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="mb-4"
            />
            <div className="flex-1 flex items-center justify-center">
              <div className="border rounded-lg p-8 text-center">
                <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-base text-muted-foreground">
                  ไม่พบข้อมูลผู้ใช้
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardContent className="p-0 h-full flex flex-col">
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-4 w-4" />
            <h3 className="font-medium">{title}</h3>
          </div>

          {/* Selected User Token Balances */}
          {selectedUserId && selectedToken && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  ยอดคงเหลือของ{' '}
                  {getSelectedUser()
                    ? getUserDisplayName(getSelectedUser()!)
                    : 'ผู้ใช้ที่เลือก'}
                </span>
              </div>
              <div className="space-y-2">
                {getSelectedUserTokens().length > 0 ? (
                  getSelectedUserTokens().map((userToken) => {
                    const token = tokens.find((t) => t.id === userToken.tokenId)
                    return (
                      <div
                        key={userToken.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="size-6">
                            <AvatarImage
                              src={token?.logoUrl || ''}
                              alt={token?.name || ''}
                              className="object-cover"
                            />
                            <AvatarFallback className="size-6 text-xs">
                              {token?.symbol?.slice(0, 2).toUpperCase() || 'TK'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">
                            {token?.symbol || 'Token'}
                          </span>
                        </div>
                        <span className="text-sm font-mono text-blue-700">
                          {fNumber(userToken.amount)}
                        </span>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-sm text-blue-600">
                    ไม่พบข้อมูลโทเคนสำหรับผู้ใช้นี้
                  </div>
                )}
              </div>
            </div>
          )}

          <Input
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="mb-4"
          />
          <div className="flex-1 flex flex-col">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>ชื่อผู้ใช้</TableHead>
                  <TableHead>อีเมล</TableHead>
                  <TableHead>รหัสสมาชิก</TableHead>
                  <TableHead>บทบาท</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const isSelected = selectedUserId === user.uid

                  return (
                    <TableRow
                      key={user.uid}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => onUserSelect(user.uid)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => onUserSelect(user.uid)}
                          className="pointer-events-none"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="size-8 rounded-lg">
                            <AvatarImage
                              src={user.imageUrl}
                              alt={getUserDisplayName(user)}
                              className="object-cover"
                            />
                            <AvatarFallback className="size-8 rounded-lg">
                              {getUserDisplayName(user)
                                .slice(0, 2)
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-sm">
                            {getUserDisplayName(user)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {user.email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {user.memberCode}
                        </span>
                      </TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <div className="mt-4">
              <CustomPagination
                currentPage={pageMeta.page}
                totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
                onPageChange={onPageChange}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
